// Deep Space night-sky backdrop — a reusable, container-filling layer for the "deep" theme scene
// cards (sibling to the spooky scenes). Ported verbatim from the design handoff's space-sky.jsx
// (ringed gas-giant, comet-moon, shooting stars, starfield, Milky-Way band, optional nebula /
// aurora / galaxy / blue planet), re-tinted teal #35e0d0 / amber #ffb454 on near-black #070b14.
// Container-relative (position:absolute; inset:0), biased right/top so the left stays clear for text.
// Raw hex here are intentional ARTWORK colours (planet/moon/comet shading), not theme tokens.
import { useMemo, useEffect } from "react";

// One-time keyframe / class injection (idempotent by id).
function useSkyStyles() {
  useEffect(() => {
    if (document.getElementById("ds-sky-styles")) return;
    const el = document.createElement("style");
    el.id = "ds-sky-styles";
    el.textContent = `
      @keyframes ds-twinkle { 0%,100%{ opacity:.25; transform:scale(.7);} 50%{ opacity:1; transform:scale(1);} }
      @keyframes ds-shoot {
        0%   { transform: rotate(var(--a,205deg)) translateX(0); opacity:0; }
        2%   { opacity:0; }
        4%   { opacity:1; }
        11%  { transform: rotate(var(--a,205deg)) translateX(var(--len,320px)); opacity:0; }
        100% { transform: rotate(var(--a,205deg)) translateX(var(--len,320px)); opacity:0; }
      }
      @keyframes ds-rmeteor {
        0%   { transform: translate(-150px, var(--y,0)); opacity:0; }
        10%  { transform: translate(-118px, var(--y,0)); opacity:.95; }
        70%  { transform: translate(105px,  var(--y,0)); opacity:.9; }
        90%  { transform: translate(142px,  var(--y,0)); opacity:.3; }
        100% { transform: translate(165px,  var(--y,0)); opacity:0; }
      }
      @keyframes ds-comet-orbit { 0%{ transform:rotate(0);} 100%{ transform:rotate(360deg);} }
      @keyframes ds-mwdrift { 0%,100%{ transform:translateX(-3%) rotate(-12deg);} 50%{ transform:translateX(3%) rotate(-12deg);} }
      @keyframes ds-planetbob { 0%,100%{ transform:translateY(0);} 50%{ transform:translateY(-6px);} }
      @keyframes ds-halopulse { 0%,100%{ opacity:.5; transform:scale(1);} 50%{ opacity:.8; transform:scale(1.06);} }
      @keyframes ds-nebuladrift { 0%,100%{ transform:translate(0,0) scale(1);} 50%{ transform:translate(-4%,2%) scale(1.06);} }
      @keyframes ds-aurora { 0%,100%{ opacity:.4; transform:translateX(-4%) skewX(-8deg);} 50%{ opacity:.78; transform:translateX(4%) skewX(-8deg);} }
      @keyframes ds-galaxyspin { 0%{ transform:rotate(0);} 100%{ transform:rotate(360deg);} }
    `;
    document.head.appendChild(el);
  }, []);
}

function Stars({ count = 70, seed = 12345 }) {
  const stars = useMemo(() => {
    let s = seed;
    const r = () => { s = (s * 1664525 + 1013904223) | 0; return ((s >>> 0) % 100000) / 100000; };
    return Array.from({ length: count }, (_, i) => ({
      id: i, top: r() * 100, left: r() * 100, size: 0.6 + r() * 1.6,
      delay: r() * 6, dur: 3 + r() * 4, op: 0.4 + r() * 0.55,
    }));
  }, [count, seed]);
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }} aria-hidden="true">
      {stars.map((st) => (
        <span key={st.id} style={{
          position: "absolute", top: `${st.top}%`, left: `${st.left}%`,
          width: `${st.size}px`, height: `${st.size}px`, borderRadius: "50%",
          background: "#eaf3ff", opacity: st.op, boxShadow: "0 0 2px rgba(200,230,255,.7)",
          animation: `ds-twinkle ${st.dur}s ease-in-out ${st.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

function MilkyBand() {
  return (
    <div aria-hidden="true" style={{
      position: "absolute", top: "-12%", right: "-20%", width: "150%", height: "62%",
      pointerEvents: "none", opacity: 0.5, mixBlendMode: "screen",
      transformOrigin: "center", animation: "ds-mwdrift 90s ease-in-out infinite",
      background:
        "radial-gradient(60% 120% at 55% 50%, rgba(120,160,235,.20), transparent 70%)," +
        "radial-gradient(38% 90% at 40% 46%, rgba(53,224,208,.14), transparent 72%)," +
        "radial-gradient(30% 80% at 68% 54%, rgba(255,180,84,.10), transparent 74%)",
      filter: "blur(6px)",
    }} />
  );
}

function ShootingStars({ count = 3, seed = 77711 }) {
  const streaks = useMemo(() => {
    let s = seed;
    const r = () => { s = (s * 1664525 + 1013904223) | 0; return ((s >>> 0) % 100000) / 100000; };
    return Array.from({ length: count }, (_, i) => ({
      id: i, top: 4 + r() * 40, left: 45 + r() * 50, angle: 200 + r() * 35,
      delay: r() * 16, cycle: 9 + r() * 12, len: 180 + r() * 200,
    }));
  }, [count, seed]);
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }} aria-hidden="true">
      {streaks.map((st) => (
        <span key={st.id} style={{
          position: "absolute", top: `${st.top}%`, left: `${st.left}%`,
          width: "120px", height: "1.6px", borderRadius: "2px", transformOrigin: "right center", opacity: 0,
          background: "linear-gradient(to left, rgba(255,250,235,1) 0%, rgba(210,232,255,.6) 32%, rgba(180,205,255,0) 100%)",
          boxShadow: "0 0 6px rgba(255,250,235,.8)", filter: "drop-shadow(0 0 4px rgba(210,232,255,.6))",
          "--a": `${st.angle}deg`, "--len": `${st.len}px`,
          animation: `ds-shoot ${st.cycle}s linear ${-st.delay}s infinite`,
        }} />
      ))}
    </div>
  );
}

function RingedPlanet() {
  return (
    <div aria-hidden="true" style={{
      position: "absolute", top: "-14%", right: "-8%", width: "62%", aspectRatio: "1/1",
      pointerEvents: "none", animation: "ds-planetbob 12s ease-in-out infinite",
    }}>
      <svg viewBox="-200 -200 400 400" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ overflow: "visible" }}>
        <defs>
          <radialGradient id="ds-planet" cx="40%" cy="38%" r="62%">
            <stop offset="0%" stopColor="#ffd79a" /><stop offset="55%" stopColor="#e79a52" /><stop offset="100%" stopColor="#7a3f1c" />
          </radialGradient>
          <radialGradient id="ds-planet-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffb454" stopOpacity="0.42" /><stop offset="100%" stopColor="#ffb454" stopOpacity="0" />
          </radialGradient>
        </defs>
        <g transform="rotate(-18)">
          <ellipse cx="0" cy="0" rx="190" ry="190" fill="url(#ds-planet-halo)" style={{ transformOrigin: "center", animation: "ds-halopulse 6s ease-in-out infinite" }} />
          <ellipse cx="0" cy="0" rx="150" ry="30" fill="none" stroke="#35e0d0" strokeWidth="3" strokeOpacity="0.4" />
          <ellipse cx="0" cy="0" rx="116" ry="21" fill="none" stroke="#7fe9df" strokeWidth="3.5" strokeOpacity="0.6" />
          <circle cx="0" cy="0" r="64" fill="url(#ds-planet)" />
          <ellipse cx="0" cy="7" rx="60" ry="9" fill="#7a3f1c" opacity="0.35" />
          <ellipse cx="0" cy="-9" rx="57" ry="6" fill="#f2b06a" opacity="0.45" />
          <ellipse cx="-14" cy="-24" rx="15" ry="4" fill="#ffe0b0" opacity="0.3" />
          <path d="M -116 4 A 116 21 0 0 0 116 4" fill="none" stroke="#9ff0e8" strokeWidth="3.5" strokeOpacity="0.85" />
          <path d="M -150 4 A 150 30 0 0 0 150 4" fill="none" stroke="#35e0d0" strokeWidth="3" strokeOpacity="0.6" />
          <g style={{ overflow: "visible" }}>
            <PlanetMeteor delay="0s" /><PlanetMeteor delay="-6s" /><PlanetMeteor delay="-12s" />
          </g>
        </g>
      </svg>
    </div>
  );
}
function PlanetMeteor({ delay = "0s" }) {
  const seed = parseFloat(delay) || 0.5;
  const r = (n) => (Math.sin(seed * 99 + n) + 1) / 2;
  const sx = 0.9 + r(1) * 1.0, sy = 0.8 + r(2) * 0.9, yOff = (r(3) - 0.5) * 30;
  return (
    <g style={{ animation: `ds-rmeteor 16s linear ${delay} infinite`, opacity: 0, "--y": `${yOff}px` }}>
      <g transform={`scale(${sx} ${sy})`}>
        <path d="M -18 0 L -4 -0.9 L -4 0.9 Z" fill="#d6c4b8" opacity="0.6" />
        <path d="M -3 -2 L 3 -2.4 L 4 0 L 2 2.4 L -2 2 L -3.5 0 Z" fill="#2a1a14" stroke="#9a6444" strokeWidth="0.4" />
      </g>
    </g>
  );
}

function CometMoon() {
  const rings = [
    { rx: 60, ry: 11, tilt: 0, count: 7, dur: 22, opacity: 0.4 },
    { rx: 80, ry: 15, tilt: -3, count: 11, dur: 28, opacity: 0.34 },
    { rx: 94, ry: 19, tilt: 4, count: 9, dur: 36, opacity: 0.3 },
  ];
  const orbitPath = (rx, ry) => `M -${rx} 0 A ${rx} ${ry} 0 0 1 ${rx} 0 A ${rx} ${ry} 0 0 1 -${rx} 0`;
  const comets = (ring, key) => Array.from({ length: ring.count }, (_, i) => {
    const phase = (i / ring.count) * ring.dur;
    return (
      <g key={`${key}-${i}`}>
        <path d="M 0 0 L -6 0" stroke="#bfe6ff" strokeWidth="0.9" strokeOpacity="0.7" strokeLinecap="round" fill="none" />
        <circle r="1.4" fill="#fff" opacity="0.95" />
        <animateMotion dur={`${ring.dur}s`} repeatCount="indefinite" path={orbitPath(ring.rx, ring.ry)} begin={`-${phase}s`} rotate="auto" />
      </g>
    );
  });
  return (
    <div aria-hidden="true" style={{ position: "absolute", top: "8%", right: "46%", width: "30%", aspectRatio: "1/1", pointerEvents: "none" }}>
      <svg viewBox="-110 -110 220 220" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ overflow: "visible" }}>
        <defs>
          <radialGradient id="ds-moon-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#d8ecff" stopOpacity="0.55" /><stop offset="100%" stopColor="#d8ecff" stopOpacity="0" />
          </radialGradient>
          <clipPath id="ds-ring-top"><rect x="-200" y="-200" width="400" height="200" /></clipPath>
          <clipPath id="ds-ring-bot"><rect x="-200" y="0" width="400" height="200" /></clipPath>
        </defs>
        <circle cx="0" cy="0" r="58" fill="url(#ds-moon-glow)" />
        <g transform="rotate(-18)">
          {rings.map((ring, idx) => (
            <g key={idx} transform={`rotate(${ring.tilt})`}>
              <g clipPath="url(#ds-ring-top)">
                <path d={orbitPath(ring.rx, ring.ry)} fill="none" stroke="#9fd8ff" strokeWidth="0.35" strokeOpacity={ring.opacity} />
                {comets(ring, `t${idx}`)}
              </g>
            </g>
          ))}
          <circle cx="0" cy="0" r="33" fill="#dfeefc" opacity="0.95" />
          <ellipse cx="-8" cy="-6" rx="4" ry="3" fill="#a9c2d8" opacity="0.7" />
          <ellipse cx="8" cy="6" rx="3.5" ry="3" fill="#a9c2d8" opacity="0.7" />
          <ellipse cx="-4" cy="11" rx="2.4" ry="2" fill="#a9c2d8" opacity="0.7" />
          {rings.map((ring, idx) => (
            <g key={`f${idx}`} transform={`rotate(${ring.tilt})`}>
              <g clipPath="url(#ds-ring-bot)">
                <path d={orbitPath(ring.rx, ring.ry)} fill="none" stroke="#9fd8ff" strokeWidth="0.35" strokeOpacity={ring.opacity} />
                {comets(ring, `b${idx}`)}
              </g>
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}

function Nebula({ hue }) {
  const h = hue || "120,90,220";
  return <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", mixBlendMode: "screen", opacity: 0.65, animation: "ds-nebuladrift 42s ease-in-out infinite", background: `radial-gradient(52% 60% at 68% 30%, rgba(${h},.36), transparent 66%), radial-gradient(46% 56% at 40% 62%, rgba(${h},.22), transparent 70%)`, filter: "blur(8px)" }} />;
}

function Aurora({ hue }) {
  const h = hue || "90,230,180";
  return <div aria-hidden="true" style={{ position: "absolute", top: "-8%", right: "-10%", width: "120%", height: "56%", pointerEvents: "none", mixBlendMode: "screen", animation: "ds-aurora 12s ease-in-out infinite", background: `radial-gradient(60% 100% at 62% 0%, rgba(${h},.3), transparent 70%), radial-gradient(42% 90% at 82% 8%, rgba(${h},.2), transparent 72%)`, filter: "blur(10px)" }} />;
}

function Galaxy() {
  return (
    <div aria-hidden="true" style={{ position: "absolute", top: "0%", right: "4%", width: "44%", aspectRatio: "1/1", pointerEvents: "none", opacity: 0.85 }}>
      <div style={{ position: "absolute", inset: 0, transform: "rotate(-26deg)" }}>
        <div style={{ position: "absolute", inset: 0, animation: "ds-galaxyspin 100s linear infinite", transformOrigin: "50% 50%", borderRadius: "50%", background: "conic-gradient(from 0deg, transparent 0deg, rgba(150,180,255,.28) 40deg, transparent 120deg, rgba(200,160,255,.26) 200deg, transparent 300deg, rgba(150,180,255,.24) 340deg, transparent 360deg)", filter: "blur(4px)", maskImage: "radial-gradient(closest-side, #000 30%, transparent 78%)", WebkitMaskImage: "radial-gradient(closest-side, #000 30%, transparent 78%)" }} />
        <div style={{ position: "absolute", left: "50%", top: "50%", width: "34%", height: "34%", transform: "translate(-50%,-50%)", borderRadius: "50%", background: "radial-gradient(closest-side, rgba(255,244,214,.95), rgba(255,210,150,.5) 45%, transparent 75%)", filter: "blur(2px)" }} />
      </div>
    </div>
  );
}

function BluePlanet() {
  return (
    <div aria-hidden="true" style={{ position: "absolute", top: "8%", right: "8%", width: "34%", aspectRatio: "1/1", pointerEvents: "none", animation: "ds-planetbob 13s ease-in-out infinite" }}>
      <svg viewBox="-100 -100 200 200" width="100%" height="100%" style={{ overflow: "visible" }}>
        <defs>
          <radialGradient id="ds-bp" cx="38%" cy="34%" r="66%"><stop offset="0%" stopColor="#a6e9ff" /><stop offset="55%" stopColor="#3f8fd0" /><stop offset="100%" stopColor="#153864" /></radialGradient>
          <radialGradient id="ds-bp-halo" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#5fa8ff" stopOpacity="0.4" /><stop offset="100%" stopColor="#5fa8ff" stopOpacity="0" /></radialGradient>
        </defs>
        <circle r="92" fill="url(#ds-bp-halo)" />
        <circle r="60" fill="url(#ds-bp)" />
        <ellipse cx="-12" cy="-20" rx="17" ry="6" fill="#d6f2ff" opacity="0.4" />
        <ellipse cx="10" cy="14" rx="26" ry="9" fill="#173a6a" opacity="0.35" />
        <path d="M-60 8 A60 60 0 0 0 60 8 A60 16 0 0 1 -60 8 Z" fill="#08152a" opacity="0.4" />
      </svg>
    </div>
  );
}

export default function SpaceSky(props = {}) {
  useSkyStyles();
  const show = (v, d) => (v === undefined ? d : v === true || v === "true");
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }} aria-hidden="true">
      {props.tint ? <div style={{ position: "absolute", inset: 0, background: props.tint }} /> : null}
      {show(props.nebula, false) && <Nebula hue={props.nebulaHue} />}
      {show(props.aurora, false) && <Aurora hue={props.auroraHue} />}
      {show(props.milky, true) && <MilkyBand />}
      <Stars count={props.starCount ? +props.starCount : 70} seed={props.seed ? +props.seed : 12345} />
      {show(props.galaxy, false) && <Galaxy />}
      {show(props.shooting, true) && <ShootingStars count={props.shootingCount ? +props.shootingCount : 3} />}
      {show(props.planet, true) && <RingedPlanet />}
      {show(props.bluePlanet, false) && <BluePlanet />}
      {show(props.moon, true) && <CometMoon />}
    </div>
  );
}
